import {create} from "@/store/createStore";
import type {Path, PathValue, TAgency} from "@/store/types";
import {set as _set} from "lodash";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

const AgencySchema = z.object({
  city: z
    .string({message: "company_profile:missing_address_city_error"})
    .min(1, "company_profile:missing_address_city_error"),
  address: z
    .string({message: "company_profile:missing_address_street_error"})
    .min(1, "company_profile:missing_address_street_error")
    .superRefine((val, ctx) => {
      if (val.length && !/\d/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "company_profile:missing_address_street_number_error",
        });
      }
    }),
  postal_code: z
    .string({message: "company_profile:missing_address_postal_error"})
    .min(1, "company_profile:missing_address_postal_error"),
  name: z.string().min(1, "agency_profile:missing_agency_name_error"),
  website: z.string().min(1, "company_profile:missing_website_error"),
});

type TState = {
  agency: TAgency;
  errors: Record<string, string> | null;
  setValue<K extends Path<TState>, V extends PathValue<TState, K>>(key: K, value: V): void;
  setAgencyValue<K extends Path<TState["agency"]>, V extends PathValue<TState["agency"], K>>(key: K, value: V): void;
  agencyInfoValidation(): boolean;
  reset(): void;
};

const initialAgencyState = {} as TAgency;

const useAgencyStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        agency: initialAgencyState,
        errors: null,
        setValue: (key, value) => {
          set(
            state => {
              _set(state, key, value);
            },
            false,
            "useAgencyStore/setValue",
          );

          const errorKey = key.split(".").pop()!;

          if (!!get().errors && errorKey in get().errors! && !!get().errors![errorKey].length) {
            set(state => {
              delete state.errors![errorKey];
            });
          }
        },
        setAgencyValue: (key, value) => {
          get().setValue(("agency." + key) as Path<TState>, value);
        },
        agencyInfoValidation: () => {
          const result = AgencySchema.safeParse(get().agency);

          if (!result.success) {
            result.error.issues.map(issue =>
              set(state => {
                if (state.errors) {
                  state.errors[issue.path[0]] = issue.message;
                } else {
                  state.errors = {[issue.path[0]]: issue.message};
                }
              }),
            );
          }

          return result.success;
        },
        reset: () =>
          set(
            state => {
              state.agency = initialAgencyState;
            },
            false,
            "useAgencyStore/reset",
          ),
      }),
      {name: "useAgencyStore"},
    ),
  ),
);

export {useAgencyStore};
